import { JSX, useLayoutEffect, useRef, useState } from "react";
import color from "../../resource/color";

interface Props {
  text: string;
  /** Lines shown before "อ่านเพิ่มเติม". */
  rows?: number;
}

/**
 * Text clamped to `rows` lines with an "อ่านเพิ่มเติม" / "ย่อ" toggle that only appears when the
 * text really runs past `rows` lines. Overflow is measured on the rendered element (and again on
 * resize, e.g. when the table column changes width), rather than antd's ellipsis, which showed
 * the toggle on short text inside the table.
 */
function ClampedText({ text, rows = 3 }: Props): JSX.Element {
  const ref = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [overflowing, setOverflowing] = useState(false);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => {
      // Only meaningful while clamped; once expanded, keep the last answer so "ย่อ" stays.
      if (!expanded) setOverflowing(el.scrollHeight > el.clientHeight + 1);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [text, rows, expanded]);

  return (
    <div>
      <div
        ref={ref}
        style={{
          fontFamily: "Sarabun",
          fontSize: 14,
          color: color.Text2,
          wordBreak: "break-word",
          whiteSpace: "pre-wrap",
          ...(expanded
            ? {}
            : {
                display: "-webkit-box",
                WebkitBoxOrient: "vertical",
                WebkitLineClamp: rows,
                overflow: "hidden",
              }),
        }}
      >
        {text}
      </div>
      {(overflowing || expanded) && (
        <span
          role='button'
          onClick={(e) => {
            e.stopPropagation();
            setExpanded((v) => !v);
          }}
          style={{ fontFamily: "Sarabun", fontSize: 14, color: color.primary, cursor: "pointer" }}
        >
          {expanded ? "ย่อ" : "อ่านเพิ่มเติม"}
        </span>
      )}
    </div>
  );
}

export default ClampedText;
